import React, { useEffect, useState } from 'react';
import HeatmapChart from '../heatmapWithSource'; // Adjust the import path as needed
import {
  PLATFORM_LIST_WITH_ALL,
  PLATFORMS_COLORS,
  SELECTED_PLATFORM_COLOR_CODE
} from '../../../config/constants';
import { fetchBestTimeToPost } from '../../../redux/slices/analytics/bestTimeToPost';
import { useDispatch, useSelector } from 'react-redux';
import IsLoadingIndicator from '../../isLoadingIndicator';
import { useTranslate } from '@tolgee/react';

type PlatformHeatmapListProps = {
  initialSource?: string;
  platforms?: string[]; // List of platforms to display heatmaps for
};

const PlatformHeatmapList: React.FC<PlatformHeatmapListProps> = ({
  initialSource = 'impressions', // Default source
  platforms = PLATFORM_LIST_WITH_ALL // Default platforms
}) => {
  const [source, setSource] = useState(initialSource);
  const DAYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday'
  ];
  const { t } = useTranslate();
  // data is const for now:
  const dispatch = useDispatch();
  const location_uuid = useSelector(
    (state: any) => state.locationDetails.uuid
  );
  useEffect(() => {
    dispatch(fetchBestTimeToPost(location_uuid) as any);
  }, [dispatch, location_uuid]);
  const { data, isLoading } = useSelector((state: any) => state.bestTimeToPost);

  const getPlatformData = (data, platform) => {
    const platformData = data?.data?.[platform];
    let currentMaxValue = 0;
    const newData = [];

    if (platformData) {
      DAYS.forEach((day, rowIndex) => {
        const dayData = platformData?.[day.toLowerCase()];
        if (dayData) {
          for (let hour = 0; hour < 24; hour++) {
            const cell = dayData?.[hour.toString()];
            const value = cell?.[source];
            newData.push([hour, rowIndex, value]);
            if (value != null && value > currentMaxValue) {
              currentMaxValue = value;
            }
          }
        } else {
          for (let hour = 0; hour < 24; hour++) {
            newData.push([hour, rowIndex, null]);
          }
        }
      });
    } else {
      return null;
    }
    return newData;
  };

  // Show loading spinner while data is loading
  if (isLoading) {
    return (
      <IsLoadingIndicator />
    );
  }

  // Only show no data placeholder after loading is complete and there's no data
  if (!data || !data.data) {
    return (
      <div className="w-100 h-100 d-flex justify-content-center align-items-center p-4">
        <div className="card shadow-sm p-5 text-center" style={{ maxWidth: '400px', fontFamily: 'Inter, sans-serif' }}>
          <i className="fa-duotone fa-chart-scatter text-primary mb-4" style={{ fontSize: '4rem' }}></i>
          <h5 className="mb-3" style={{ fontSize: '1.25rem' }}>{t('best_time_to_post.no_data_available')}</h5>
          <p className="text-muted" style={{ fontSize: '1.05rem' }}>{t('best_time_to_post.no_data_available_explanation')}</p>
        </div>
      </div>
    );
  }
  return (
    <div>
      {platforms.map(
        (platform) =>
          getPlatformData(data, platform) && (
            <HeatmapChart
              key={platform}
              source={source}
              color={SELECTED_PLATFORM_COLOR_CODE[platform]}
              chartData={getPlatformData(data, platform)}
              platform={platform}
              onSourceChange={(newSource) => setSource(newSource)}
            />
          )
      )}

      {/* check if all platforms have no data - only show after loading is complete */}
      {!isLoading && platforms.every((platform) => !getPlatformData(data, platform)) && (
        <div className="w-100 h-400px d-flex justify-content-center align-items-center p-4">
          <div className="card shadow-sm p-5 text-center" style={{ maxWidth: '400px', fontFamily: 'Inter, sans-serif' }}>
            <i className="fa-duotone fa-chart-scatter text-primary mb-4" style={{ fontSize: '4rem' }}></i>
            <h5 className="mb-3" style={{ fontSize: '1.25rem' }}>{t('best_time_to_post.no_data_available')}</h5>
            <p className="text-muted" style={{ fontSize: '1.05rem' }}>No analytics data is currently available for this selection.</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformHeatmapList;
